import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, RotateCcw, ArrowRight, Sparkles } from "lucide-react";
import { feedbackForScore } from "@/lib/scoring";

function StarRow({ filled }) {
  return (
    <div className="flex items-center justify-center gap-2 my-4" aria-label={`${filled} of 3 stars`}>
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          initial={{ scale: 0, rotate: -30 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.15 + i * 0.15, type: "spring", stiffness: 300 }}
        >
          <Star
            size={52}
            strokeWidth={2}
            className={i < filled ? "" : "opacity-30"}
            fill={i < filled ? "#F59E0B" : "none"}
            color={i < filled ? "#F59E0B" : "#94A3B8"}
          />
        </motion.div>
      ))}
    </div>
  );
}

export function ScoreDisplay({ open, result, passed, mastered, onRetry, onContinue, continueLabel }) {
  if (!result) return null;
  const fb = feedbackForScore(result.score);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1F2937]/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="score-display-overlay"
        >
          <motion.div
            className="w-full max-w-md rounded-[28px] bg-white p-8 shadow-2xl border border-[#E5DECF] text-center"
            initial={{ scale: 0.85, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
          >
            {mastered && (
              <div className="inline-flex items-center gap-2 rounded-full bg-[#10B981]/15 px-4 py-1.5 text-[#047857] font-bold text-sm mb-3">
                <Sparkles size={16} /> Letter Mastered!
              </div>
            )}
            <StarRow filled={fb.stars} />
            <h3 className="font-heading text-2xl font-extrabold text-[#1F2937]" data-testid="score-title">
              {fb.title}
            </h3>
            <p className="mt-2 text-[#6B7280] font-body">
              You scored <span className="font-bold text-[#0D9488]" data-testid="score-value">{result.score}%</span>
            </p>

            <div className="mt-5 grid grid-cols-2 gap-2 text-left">
              {[
                ["Path", result.pathAccuracy],
                ["Coverage", result.completion],
                ["Start", result.startingPosition],
                ["Direction", result.direction],
              ].map(([label, val]) => (
                <div key={label} className="rounded-xl bg-[#F2ECE1] px-3 py-2">
                  <div className="text-[11px] uppercase tracking-wide text-[#6B7280] font-semibold">{label}</div>
                  <div className="font-heading font-bold text-[#1F2937]">{val}%</div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-3">
              {passed ? (
                <button
                  onClick={onContinue}
                  data-testid="score-continue-button"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#0D9488] px-6 py-4 text-lg font-bold text-white shadow-md transition-all hover:bg-[#0B7F74] hover:shadow-lg active:translate-y-0.5"
                >
                  {continueLabel || "Continue"} <ArrowRight size={20} />
                </button>
              ) : null}
              <button
                onClick={onRetry}
                data-testid="score-retry-button"
                className={`inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-4 text-lg font-bold transition-all active:translate-y-0.5 ${
                  passed
                    ? "bg-[#F2ECE1] text-[#1F2937] hover:bg-[#E5DECF]"
                    : "bg-[#F59E0B] text-white shadow-md hover:bg-[#D97706] hover:shadow-lg"
                }`}
              >
                <RotateCcw size={20} /> Try Again
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
